import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, View } from 'react-native';
import Svg, {
	Defs,
	Image as SvgImage,
	LinearGradient,
	Rect,
	Stop,
	Text as SvgText,
} from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import ChipPng from '../../../assets/chip.png';
import {
	miniGamesRewardsRequest,
	miniGamesRewardsStartPolling,
	miniGamesRewardsStopPolling,
} from '../../../store/jackpot/miniGamesRewardsActions';
import {
	selectMiniGamesBronzeAmount,
	selectMiniGamesGoldAmount,
	selectMiniGamesSilverAmount,
} from '../../../store/jackpot/miniGamesRewardsReducer';
import { FormatWithThousandSeparator } from '../../../utils/helpers/numberFormat';
import { useDevice } from '../../../utils/hooks/useDevice';
import { JackpotTier, useJackpotCycle } from '../../../utils/hooks/useJackpotCycle';
import { useJackpotTween } from '../../../utils/hooks/useJackpotTween';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../../Components/common/Touchable';
import JackpotHistoryModal from '../../Components/Modals/JackpotHistoryModal';

const BANNER_ART = { width: 360, height: 37 };
const LABEL_X = 16;
const LABEL_MAX_WIDTH = 140;
const AMOUNT_X = 344;

/**
 * Height the banner takes in AppFooter's mobile stack, padding included. The home redesign parks
 * its recent-tables bar right on top of that stack, so it has to measure it the same way.
 */
export const getMobileJackpotBlockHeight = (viewportWidth: number) =>
	5 + (Math.min(viewportWidth - 30, 390) * BANNER_ART.height) / BANNER_ART.width;

/**
 * The jackpot figure as the banner shows it: polled while mounted, tweened between updates and
 * formatted with thousands separators. Shared with the desktop jackpot bar.
 */
export const useJackpotAmounts = () => {
	const dispatch = useDispatch();
	const goldAmount = useSelector(selectMiniGamesGoldAmount);
	const silverAmount = useSelector(selectMiniGamesSilverAmount);
	const bronzeAmount = useSelector(selectMiniGamesBronzeAmount);
	const tweenedGoldAmount = useJackpotTween(goldAmount, 1200);
	const tweenedSilverAmount = useJackpotTween(silverAmount, 1200);
	const tweenedBronzeAmount = useJackpotTween(bronzeAmount, 1200);

	useEffect(() => {
		dispatch(miniGamesRewardsRequest());
		dispatch(miniGamesRewardsStartPolling());
		return () => {
			dispatch(miniGamesRewardsStopPolling());
		};
	}, [dispatch]);

	return {
		gold: FormatWithThousandSeparator(Math.floor(tweenedGoldAmount), {
			hideFractIfInteger: true,
		}),
		silver: FormatWithThousandSeparator(Math.floor(tweenedSilverAmount), {
			hideFractIfInteger: true,
		}),
		bronze: FormatWithThousandSeparator(Math.floor(tweenedBronzeAmount), {
			hideFractIfInteger: true,
		}),
	};
};

const TopJackpotBanner = ({ width }: { width?: number } = {}) => {
	const { t } = useTranslation();
	const { safeAreaWidth, isMobile, isMd } = useDevice();
	const viewportWidth = width ?? safeAreaWidth;
	const bannerWidth =
		width != null
			? viewportWidth
			: Math.min(viewportWidth - (isMobile ? 30 : isMd ? 120 : 180), isMobile ? 390 : 540);
	const bannerHeight = bannerWidth * (BANNER_ART.height / BANNER_ART.width);
	const styles = createStyles(viewportWidth, isMobile, isMd, width != null);
	const borderId = useSvgId('jackpotBorder');
	const amounts = useJackpotAmounts();
	const { current, next, offset } = useJackpotCycle(amounts);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const jackpotLabel = t('T_Jackpot', { defaultValue: 'JACKPOT' });
	const renderTierText = (tier: JackpotTier, index: number) => {
		const label = `${jackpotLabel} ${tier.number}`;
		const labelFontSize = Math.min(26, LABEL_MAX_WIDTH / Math.max(1, label.length * 0.6));
		const amountFontSize = tier.amount.length > 11 ? 21 : tier.amount.length > 9 ? 23 : 26;

		return (
			<View key={index} style={styles.tierRow}>
				<Svg width='100%' height='100%' viewBox='0 0 360 37'>
					<SvgText
						x={LABEL_X}
						y='26.2'
						fill='#421000'
						fontFamily='Sofia Sans'
						fontSize={labelFontSize}
						fontWeight='700'
						letterSpacing='0.4'
					>
						{label}
					</SvgText>
					<SvgText
						x={LABEL_X}
						y='25.4'
						fill={tier.color}
						fontFamily='Sofia Sans'
						fontSize={labelFontSize}
						fontWeight='700'
						letterSpacing='0.4'
					>
						{label}
					</SvgText>
					<SvgText
						x={AMOUNT_X}
						y='26.2'
						fill='#421000'
						fontFamily='Sofia Sans'
						fontSize={amountFontSize}
						fontWeight='700'
						textAnchor='end'
						letterSpacing='0.3'
					>
						{tier.amount}
					</SvgText>
					<SvgText
						x={AMOUNT_X}
						y='25.4'
						fill={tier.color}
						fontFamily='Sofia Sans'
						fontSize={amountFontSize}
						fontWeight='700'
						textAnchor='end'
						letterSpacing='0.3'
					>
						{tier.amount}
					</SvgText>
				</Svg>
			</View>
		);
	};

	return (
		// box-none: only the banner itself takes the tap, not the padding around it.
		<View pointerEvents='box-none' style={styles.outerGlow}>
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_SITE_JACKPOT_HISTORY', { defaultValue: 'Jackpot history' })}
				onPress={() => setIsHistoryOpen(true)}
				style={styles.banner}
			>
				<Svg width='100%' height='100%' viewBox='0 0 360 37' style={styles.base}>
					<Defs>
						<LinearGradient id={borderId} x1='0' x2='0' y1='0' y2='1'>
							<Stop offset='0' stopColor='#FFF9A5' />
							<Stop offset='0.52' stopColor='#FFB72D' />
							<Stop offset='1' stopColor='#C74700' />
						</LinearGradient>
					</Defs>
					<Rect x='0.5' y='0.5' width='359' height='36' rx='11' fill='#210701' />
					<Rect
						x='0.5'
						y='0.5'
						width='359'
						height='36'
						rx='11'
						stroke={`url(#${borderId})`}
						strokeWidth='1'
					/>
					<Rect x='2' y='2' width='356' height='33' rx='10' stroke='#FF2E00' strokeOpacity='0.45' />
					<SvgImage
						href={ChipPng}
						x='163.8'
						y='2'
						width='32.4'
						height='33'
						preserveAspectRatio='xMidYMid meet'
					/>
				</Svg>
				<Animated.View
					style={[
						styles.tierStack,
						{
							transform: [
								{
									translateY: offset.interpolate({
										inputRange: [0, 1],
										outputRange: [0, -bannerHeight],
									}),
								},
							],
						},
					]}
				>
					{renderTierText(current, 0)}
					{renderTierText(next, 1)}
				</Animated.View>
			</Pressable>
			{isHistoryOpen ? <JackpotHistoryModal onClose={() => setIsHistoryOpen(false)} /> : null}
		</View>
	);
};

const createStyles = (width: number, isMobile: boolean, isMd: boolean, exactWidth: boolean) => {
	const mobile = isMobile;
	// An explicit width means the caller laid the banner out itself (the lobby stacks it in
	// its scroll content), so the viewport-derived insets must not shrink it further.
	const bannerWidth = exactWidth
		? width
		: Math.min(width - (mobile ? 30 : isMd ? 120 : 180), mobile ? 390 : 540);
	const bannerHeight = bannerWidth * (37 / 360);

	return StyleSheet.create({
		outerGlow: {
			width: exactWidth ? bannerWidth : '100%',
			alignItems: 'center',
			paddingTop: exactWidth ? 0 : mobile ? 5 : 10,
			paddingHorizontal: exactWidth ? 0 : mobile ? 15 : 32,
			zIndex: 40,
			elevation: 40,
		},
		banner: {
			width: bannerWidth,
			height: bannerHeight,
			overflow: 'hidden',
		},
		base: { position: 'absolute', top: 0, left: 0 },
		tierStack: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: bannerWidth,
			height: bannerHeight * 2,
		},
		tierRow: {
			width: bannerWidth,
			height: bannerHeight,
		},
	});
};

export default TopJackpotBanner;
